import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { apiClient } from '../api/client';
import { hapticFeedback, notificationFeedback, isTelegramWebApp } from '../utils/telegram';
import type { HairQuizAnswers, HairQuizVariant } from '../types';
import { Loader } from '../components/Loader';
import { GlowCard } from '../components/ui/glow-card';
import { PremiumButton } from '../components/ui/PremiumButton';
import { getHairstyleConfig } from '../config/hairstyles';

interface HairQuizPageProps {
  onVariantSelect: (variant: HairQuizVariant) => void;
  onBack?: () => void;
}

const QUESTIONS = [
  {
    id: 'styling_direction',
    label: 'Как вы обычно укладываете волосы?',
    options: [
      { value: 'back', label: 'Назад', icon: '' },
      { value: 'forward', label: 'Вперёд', icon: '' },
      { value: 'side', label: 'На бок', icon: '' },
      { value: 'none', label: 'Не укладываю', icon: '' },
    ],
  },
  {
    id: 'fringe_preference',
    label: 'Предпочитаете закрывать лоб?',
    options: [
      { value: 'yes', label: 'Да, с чёлкой', icon: '' },
      { value: 'no', label: 'Нет, лоб открыт', icon: '' },
      { value: 'unsure', label: 'Не уверен', icon: '' },
    ],
  },
  {
    id: 'top_length',
    label: 'Длина волос сверху?',
    options: [
      { value: 'short', label: 'Короткая (до 3 см)', icon: '' },
      { value: 'medium', label: 'Средняя (3–7 см)', icon: '' },
      { value: 'long', label: 'Длинная (7+ см)', icon: '' },
    ],
  },
  {
    id: 'sides_style',
    label: 'Как стричь бока и затылок?',
    options: [
      { value: 'skin', label: 'Очень коротко (skin fade)', icon: '' },
      { value: 'short_fade', label: 'Коротко (низкий/средний фейд)', icon: '' },
      { value: 'medium', label: 'Средняя длина', icon: '' },
      { value: 'long_sides', label: 'Длинно (мягкий переход)', icon: '' },
      { value: 'natural', label: 'Естественно (без фейда)', icon: '' },
    ],
  },
  {
    id: 'styling_time',
    label: 'Время на укладку?',
    options: [
      { value: 'minimal', label: '0–2 минуты', icon: '' },
      { value: 'moderate', label: '3–7 минут', icon: '' },
      { value: 'extended', label: '10+ минут', icon: '' },
    ],
  },
  {
    id: 'hair_type',
    label: 'Текстура волос?',
    options: [
      { value: 'straight', label: 'Прямые', icon: '' },
      { value: 'wavy', label: 'Волнистые', icon: '' },
      { value: 'curly', label: 'Кудрявые', icon: '' },
    ],
  },
  {
    id: 'face_shape',
    label: 'Форма лица?',
    options: [
      { value: 'oval', label: 'Овальное', icon: '' },
      { value: 'round', label: 'Круглое', icon: '' },
      { value: 'square', label: 'Квадратное', icon: '' },
      { value: 'rectangular', label: 'Прямоугольное', icon: '' },
      { value: 'triangle', label: 'Треугольное', icon: '' },
      { value: 'heart', label: 'Сердцевидное', icon: '' },
    ],
  },
  {
    id: 'beard',
    label: 'Стиль бороды?',
    options: [
      { value: 'none', label: 'Без бороды', icon: '' },
      { value: 'stubble', label: 'Щетина', icon: '' },
      { value: 'full', label: 'Средняя/длинная', icon: '' },
    ],
  },
];

export const HairQuizPage: React.FC<HairQuizPageProps> = ({ onVariantSelect, onBack }) => {
  const [answers, setAnswers] = useState<Partial<HairQuizAnswers>>({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [variants, setVariants] = useState<HairQuizVariant[]>([]);
  const [showResults, setShowResults] = useState(false);

  const currentQuestion = QUESTIONS[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === QUESTIONS.length - 1;
  const isAllAnswered = QUESTIONS.every((q) => answers[q.id as keyof HairQuizAnswers]);

  const handleAnswer = (value: string) => {
    setAnswers((prev) => ({
      ...prev,
      [currentQuestion.id]: value,
    }));
    if (isTelegramWebApp()) hapticFeedback('light');

    setTimeout(() => {
      if (!isLastQuestion) {
        setCurrentQuestionIndex((prev) => prev + 1);
      }
    }, 300);
  };

  const handleBack = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex((prev) => prev - 1);
      if (isTelegramWebApp()) hapticFeedback('light');
    }
  };

  const handleSubmit = async () => {
    if (!isAllAnswered) {
      toast.error('Пожалуйста, ответьте на все вопросы,\nчтобы мы могли подобрать лучший стиль.');
      return;
    }

    setIsLoading(true);
    if (isTelegramWebApp()) hapticFeedback('heavy');

    try {
      const response = await apiClient.suggestHaircutsByQuiz(answers as HairQuizAnswers);

      if (response.variants && response.variants.length > 0) {
        setVariants(response.variants);
        setShowResults(true);
        const count = response.variants.length;
        const word = count === 1 ? 'вариант' : count < 5 ? 'варианта' : 'вариантов';
        toast.success(`Отлично! Мы нашли ${count} ${word}.`);
        if (isTelegramWebApp()) notificationFeedback('success');
      } else {
        toast.error('Мы не нашли идеальных совпадений.\nПопробуйте немного изменить ответы.');
        if (isTelegramWebApp()) notificationFeedback('error');
      }
    } catch (error: unknown) {
      console.error('Error in quiz:', error);
      const err = error as { detail?: string; message?: string };
      const message = err?.detail || err?.message || 'Возникла небольшая заминка.\nПопробуйте ещё раз.';
      toast.error(message, { duration: 5000 });
      if (isTelegramWebApp()) notificationFeedback('error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVariantSelect = (variant: HairQuizVariant) => {
    if (isTelegramWebApp()) hapticFeedback('medium');
    onVariantSelect(variant);
  };

  // Results View
  if (showResults && variants.length > 0) {
    return (
      <div className="space-y-6 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-2"
        >
          <h2 className="text-2xl font-medium text-white tracking-tight">
            Найдено {variants.length} {variants.length === 1 ? 'вариант' : variants.length < 5 ? 'варианта' : 'вариантов'}
          </h2>
          <p className="text-sm text-sheriff-text-secondary">
            Выберите стиль, чтобы увидеть результат
          </p>
        </motion.div>

        <div className="space-y-4">
          <AnimatePresence>
            {variants.map((variant, index) => (
              <motion.div
                key={variant.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.1 }}
                style={{ touchAction: 'pan-y' }}
              >
                <GlowCard 
                  glowColor="gold" 
                  customSize 
                  className="cursor-pointer overflow-hidden"
                >
                  <motion.div
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    onClick={() => handleVariantSelect(variant)}
                    className="relative"
                  >

                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          {(() => {
                            const variantStyleId = variant.style_id || variant.id;
                            const styleConfig = variantStyleId ? getHairstyleConfig(variantStyleId) : null;
                            const displayTitle = styleConfig?.title || variant.title;
                            return (
                              <>
                                <h3 className="text-lg font-medium text-white mb-2 tracking-tight leading-snug">{displayTitle}</h3>
                                {styleConfig?.shortDescription && (
                                  <p className="text-sm text-white/65 mb-2 leading-relaxed">
                                    {styleConfig.shortDescription}
                                  </p>
                                )}
                              </>
                            );
                          })()}
                          {(variant.reason || variant.description) && (
                            <p className="text-xs text-white/50 mb-3 leading-relaxed">
                              {variant.reason || variant.description}
                            </p>
                          )}
                          {(variant.tags && variant.tags.length > 0) && (
                            <div className="flex flex-wrap gap-2">
                              {variant.tags.map((tag, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-1 rounded-full bg-white/5 text-white/60 text-[10px] font-medium border border-white/10"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        {!variant.reference_image_url && (
                          <div className="text-[10px] text-white/30 leading-tight text-right max-w-[80px]">
                            Предпросмотр будет доступен после генерации
                          </div>
                        )}
                      </div>
                      <PremiumButton
                        fullWidth
                        variant="primary"
                        onClick={() => handleVariantSelect(variant)}
                      >
                        Примерить на мне
                      </PremiumButton>
                    </div>
                  </motion.div>
                </GlowCard>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {onBack && (
          <PremiumButton
            fullWidth
            variant="secondary"
            onClick={onBack}
          >
            Назад к вопросам
          </PremiumButton>
        )}
      </div>
    );
  }

  // Loading View
  if (isLoading) {
    return (
      <Loader text="Подбираем стрижки..." />
    );
  }

  // Quiz View
  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-2"
      >
        <h2 className="text-2xl font-medium text-white tracking-tight">Подбор стиля</h2>
        <p className="text-sm text-sheriff-text-secondary">
          Ответьте на несколько вопросов для персональных рекомендаций
        </p>
        
        {/* Progress Bar */}
        <div className="pt-3">
          <div className="flex items-center justify-center gap-3 text-xs text-sheriff-muted">
            <span>Вопрос {currentQuestionIndex + 1} из {QUESTIONS.length}</span>
          </div>
          <div className="flex gap-2 mt-3 justify-center">
            {QUESTIONS.map((_, i) => (
              <motion.div
                key={i}
                initial={false}
                animate={{
                  opacity: i <= currentQuestionIndex ? 1 : 0.25,
                }}
                transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                className="h-1 w-8 rounded-full relative overflow-hidden"
                style={{
                  background: i <= currentQuestionIndex 
                    ? 'linear-gradient(90deg, rgba(139,115,85,0.6) 0%, rgba(111,90,62,0.5) 100%)'
                    : 'rgba(255,255,255,0.08)',
                  boxShadow: i <= currentQuestionIndex 
                    ? '0 0 8px rgba(139,115,85,0.15), inset 0 1px 0 rgba(139,115,85,0.2)'
                    : 'inset 0 1px 0 rgba(255,255,255,0.03)'
                }}
              />
            ))}
          </div>
        </div>
      </motion.div>

      {/* Question Card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentQuestionIndex}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <GlowCard glowColor="gold" customSize className="overflow-visible">
            <h3 className="text-lg font-medium text-white mb-4 tracking-tight">{currentQuestion.label}</h3>
            <div className="space-y-2.5">
              {currentQuestion.options.map((option) => {
                const isSelected = answers[currentQuestion.id as keyof HairQuizAnswers] === option.value;
                return (
                  <motion.button
                    key={option.value}
                    whileHover={{ scale: 1.001, y: -0.5 }}
                    whileTap={{ scale: 0.985 }}
                    onClick={() => handleAnswer(option.value)}
                    className="w-full text-left p-4 rounded-2xl"
                    style={{
                      background: isSelected
                        ? 'linear-gradient(145deg, rgba(14,14,14,0.98) 0%, rgba(20,20,20,0.95) 100%)'
                        : 'linear-gradient(145deg, rgba(14,14,14,0.95) 0%, rgba(20,20,20,0.92) 100%)',
                      border: isSelected
                        ? '1px solid rgba(255,255,255,0.12)'
                        : '1px solid rgba(255,255,255,0.06)',
                      boxShadow: isSelected
                        ? 'inset 0 1px 0 rgba(255,255,255,0.06), 0 2px 8px rgba(0,0,0,0.35)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.04), 0 2px 8px rgba(0,0,0,0.3)',
                      transition: 'all 220ms cubic-bezier(0.25, 0.1, 0.25, 1)',
                      willChange: 'transform'
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-1.5 h-1.5 rounded-full transition-all duration-220"
                        style={{
                          background: isSelected ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.2)',
                          boxShadow: isSelected ? '0 0 4px rgba(255,255,255,0.3)' : 'none'
                        }}
                      />
                      <span className="font-medium flex-1" style={{ color: isSelected ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.75)' }}>
                        {option.label}
                      </span>
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ 
                            type: "spring",
                            stiffness: 450,
                            damping: 28
                          }}
                          className="w-5 h-5 rounded-full flex items-center justify-center"
                          style={{
                            background: 'rgba(255,255,255,0.1)',
                            border: '1px solid rgba(255,255,255,0.2)'
                          }}
                        >
                          <span style={{ color: 'rgba(255,255,255,0.85)', fontSize: '11px', fontWeight: 600 }}>✓</span>
                        </motion.div>
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </div>

            {/* Back Button */}
            {currentQuestionIndex > 0 && (
              <div className="mt-6">
                <PremiumButton
                  variant="secondary"
                  fullWidth
                  onClick={handleBack}
                  className="py-2 text-sm h-auto"
                >
                  ← Вернуться назад
                </PremiumButton>
              </div>
            )}
          </GlowCard>
        </motion.div>
      </AnimatePresence>

      {/* Submit Button */}
      {isLastQuestion && isAllAnswered && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="fixed inset-x-0 bottom-20 flex justify-center pointer-events-none px-4"
        >
          <div className="w-full max-w-md pointer-events-auto">
            <PremiumButton
              fullWidth
              variant="primary"
              onClick={handleSubmit}
            >
              Подобрать стрижки
            </PremiumButton>
          </div>
        </motion.div>
      )}
    </div>
  );
};

